import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { IconStaffShield } from "../../components/socialIcons";
import { api, errorMessage } from "../../lib/api";
import { fullDate } from "../../lib/format";
import { useMe } from "../auth/AuthProvider";

/*
 * What an account sees after Atrium staff act on it: each warning once, until
 * acknowledged, and a restriction once per session (writes also fail with the reason).
 */

const seenKey = (until: string) => `atrium:restriction-seen:${until}`;

export function AccountNotices() {
  const me = useMe();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [restrictionSeen, setRestrictionSeen] = useState(() => !me.restrictedUntil || sessionStorage.getItem(seenKey(me.restrictedUntil)) === "1");

  const notice = me.notices?.[0];
  if (notice) {
    const ack = async () => {
      setBusy(true);
      try {
        await api.post(`/api/me/notices/${notice.id}/ack`);
        await qc.invalidateQueries({ queryKey: ["me"] });
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        setBusy(false);
      }
    };
    return (
      <Dialog
        open
        onOpenChange={() => {}}
        title={
          <span className="flex items-center gap-2">
            <IconStaffShield className="size-5 text-staff" aria-hidden />
            A warning from Atrium staff
          </span>
        }
        description={`Sent ${fullDate(notice.createdAt)}`}
        footer={
          <Button variant="primary" loading={busy} onClick={() => void ack()}>
            I understand
          </Button>
        }
      >
        <p className="whitespace-pre-wrap break-words text-[15px] text-fg">{notice.reason}</p>
        <p className="mt-4 text-sm text-fg-3">Further breaks of the community guidelines can lead to your account being restricted or suspended.</p>
      </Dialog>
    );
  }

  if (me.restrictedUntil && !restrictionSeen) {
    const until = me.restrictedUntil;
    const indefinite = new Date(until).getFullYear() - new Date().getFullYear() > 50;
    const close = () => {
      sessionStorage.setItem(seenKey(until), "1");
      setRestrictionSeen(true);
    };
    return (
      <Dialog
        open
        onOpenChange={(v) => !v && close()}
        title="Your account is read-only"
        description={indefinite ? "Until Atrium staff lift the restriction." : `Until ${fullDate(until)}.`}
        footer={
          <Button variant="primary" onClick={close}>
            OK
          </Button>
        }
      >
        {me.restrictionReason && <p className="whitespace-pre-wrap break-words text-[15px] text-fg">{me.restrictionReason}</p>}
        <p className="mt-4 text-sm text-fg-3">You can still read, browse and change your settings, but you can't post, message, react or join communities.</p>
      </Dialog>
    );
  }
  return null;
}
